import React, { useEffect, useState } from "react";
import { Marker, InfoWindow } from "@react-google-maps/api";

import IconCriticalPoints from "../../assets/svg/maps/Icon_critical_points2.svg";
import IconSuddenAcceleration from "../../assets/svg/maps/Icon_sudden_aceleration.svg";
import IconSuddenBraking from "../../assets/svg/maps/Icon_sudden_braking.svg";
import IconSuddenSpeed from "../../assets/svg/maps/Icon_sudden_speed.svg";
import PropTypes from "prop-types";

const EventMarkers = ({ trip, filters }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markers, setMarkers] = useState({
    speedViolations: [],
    criticalPoints: [],
    harshAcceleration: [],
    harshBraking: [],
  });

  useEffect(() => {
    if (!trip) return;

    // Procesar excesos de velocidad con validación segura
    const speedViolations = Array.isArray(trip.overspeedEvents)
      ? trip.overspeedEvents.map((event, idx) => ({
          id: `speed-${idx}`,
          position: { lat: event.lat, lng: event.lon },
          type: "speed",
          details: {
            velocidad: event.speed,
            hora: event.dateTime?.split(" ")[1] || "N/A",
            fecha: event.dateTime?.split(" ")[0] || "N/A",
            direccion: event.direccion || "No disponible",
          },
        }))
      : [];

    // Procesar violaciones a puntos críticos con validación segura
    const criticalPoints = Array.isArray(trip.criticalViolations)
      ? trip.criticalViolations.map((violation, idx) => ({
          id: `critical-${idx}`,
          position: { lat: violation.lat, lng: violation.lon },
          type: "critical",
          details: {
            velocidad: violation.speed,
            hora: violation.dateTime?.split(" ")[1] || "N/A",
            fecha: violation.dateTime?.split(" ")[0] || "N/A",
            puntoCritico: violation.pointName || "No especificado",
          },
        }))
      : [];

    // Procesar aceleraciones bruscas con validación segura
    const harshAcceleration = Array.isArray(trip.harshAccelerationEvents)
      ? trip.harshAccelerationEvents.map((event, idx) => ({
          id: `accel-${idx}`,
          position: { lat: event.lat, lng: event.lon },
          type: "acceleration",
          details: {
            velocidad: event.speed,
            hora: event.dateTime?.split(" ")[1] || "N/A",
            fecha: event.dateTime?.split(" ")[0] || "N/A",
            direccion: event.direccion || "No disponible",
          },
        }))
      : [];

    // Procesar frenadas bruscas con validación segura
    const harshBraking = Array.isArray(trip.harshBrakingEvents)
      ? trip.harshBrakingEvents.map((event, idx) => ({
          id: `brake-${idx}`,
          position: { lat: event.lat, lng: event.lon },
          type: "braking",
          details: {
            velocidad: event.speed,
            hora: event.dateTime?.split(" ")[1] || "N/A",
            fecha: event.dateTime?.split(" ")[0] || "N/A",
            direccion: event.direccion || "No disponible",
          },
        }))
      : [];

    // Logging para depuración
    console.debug(`EventMarkers - Eventos procesados:`, {
      speedViolations: speedViolations.length,
      criticalPoints: criticalPoints.length,
      harshAcceleration: harshAcceleration.length,
      harshBraking: harshBraking.length,
    });

    setMarkers({
      speedViolations,
      criticalPoints,
      harshAcceleration,
      harshBraking,
    });
  }, [trip]);

  // Función para obtener el icono según el tipo de evento
  const getMarkerIcon = (type) => {
    const iconConfig = {
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16),
    };

    switch (type) {
      case "speed":
        return {
          url: IconSuddenSpeed,
          ...iconConfig,
        };
      case "critical":
        return {
          url: IconCriticalPoints,
          ...iconConfig,
        };
      case "acceleration":
        return {
          url: IconSuddenAcceleration,
          ...iconConfig,
        };
      case "braking":
        return {
          url: IconSuddenBraking,
          ...iconConfig,
        };
      default:
        return {
          url: IconSuddenSpeed,
          ...iconConfig,
        };
    }
  };

  // Función para obtener el título según el tipo de evento
  const getMarkerTitle = (type, details) => {
    switch (type) {
      case "speed":
        return `Exceso de velocidad: ${details.velocidad} km/h`;
      case "critical":
        return `Violación Punto Crítico: ${details.puntoCritico}`;
      case "acceleration":
        return "Aceleración Brusca";
      case "braking":
        return "Frenada Brusca";
      default:
        return "Evento";
    }
  };

  return (
    <>
      {/* Renderizar marcadores de excesos de velocidad */}
      {filters.showSpeedViolations &&
        markers.speedViolations.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={getMarkerIcon(marker.type)}
            title={getMarkerTitle(marker.type, marker.details)}
            onClick={() => setSelectedMarker(marker)}
          />
        ))}

      {/* Renderizar marcadores de puntos críticos */}
      {filters.showCriticalPoints &&
        markers.criticalPoints.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={getMarkerIcon(marker.type)}
            title={getMarkerTitle(marker.type, marker.details)}
            onClick={() => setSelectedMarker(marker)}
          />
        ))}

      {/* Renderizar marcadores de aceleración brusca */}
      {filters.showHarshAccel &&
        markers.harshAcceleration.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={getMarkerIcon(marker.type)}
            title={getMarkerTitle(marker.type, marker.details)}
            onClick={() => setSelectedMarker(marker)}
          />
        ))}

      {/* Renderizar marcadores de frenada brusca */}
      {filters.showHarshBraking &&
        markers.harshBraking.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={getMarkerIcon(marker.type)}
            title={getMarkerTitle(marker.type, marker.details)}
            onClick={() => setSelectedMarker(marker)}
          />
        ))}

      {/* Ventana de información al hacer clic en un marcador */}
      {selectedMarker && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div className="event-info-window">
            <h3>
              {getMarkerTitle(selectedMarker.type, selectedMarker.details)}
            </h3>
            <div className="event-details">
              <p>
                <strong>Fecha:</strong> {selectedMarker.details.fecha}
              </p>
              <p>
                <strong>Hora:</strong> {selectedMarker.details.hora}
              </p>
              <p>
                <strong>Velocidad:</strong> {selectedMarker.details.velocidad}{" "}
                km/h
              </p>
              {selectedMarker.type === "critical" && (
                <p>
                  <strong>Punto Crítico:</strong>{" "}
                  {selectedMarker.details.puntoCritico}
                </p>
              )}
              {(selectedMarker.type === "speed" ||
                selectedMarker.type === "acceleration" ||
                selectedMarker.type === "braking") && (
                <p>
                  <strong>Dirección:</strong>{" "}
                  {selectedMarker.details.direccion || "No disponible"}
                </p>
              )}
              <p>
                <a
                  href={`https://www.google.com/maps?q=${selectedMarker.position.lat},${selectedMarker.position.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver en Google Maps
                </a>
              </p>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
};
EventMarkers.propTypes = {
  trip: PropTypes.shape({
    overspeedEvents: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        speed: PropTypes.number,
        dateTime: PropTypes.string,
        direccion: PropTypes.string,
      })
    ),
    criticalViolations: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        speed: PropTypes.number,
        dateTime: PropTypes.string,
        pointName: PropTypes.string,
      })
    ),
    harshAccelerationEvents: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        speed: PropTypes.number,
        dateTime: PropTypes.string,
        direccion: PropTypes.string,
      })
    ),
    harshBrakingEvents: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        speed: PropTypes.number,
        dateTime: PropTypes.string,
        direccion: PropTypes.string,
      })
    ),
  }),
  filters: PropTypes.shape({
    showSpeedViolations: PropTypes.bool,
    showCriticalPoints: PropTypes.bool,
    showHarshAccel: PropTypes.bool,
    showHarshBraking: PropTypes.bool,
  }).isRequired,
};
export default EventMarkers;
